import { useState, useEffect } from 'react';
import { apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Progress } from '@shared/components/ui/progress';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import SetupNavbar from '@/components/SetupNavbar';
import { useOnboarding } from '@/contexts/OnboardingContext';

// Import step components
import BusinessInfoStep from '@/components/setup-wizard/BusinessInfoStep';
import ServiceConfigStep from '@/components/setup-wizard/ServiceConfigStep';
import InventorySetupStep from '@/components/setup-wizard/InventorySetupStep';
import ReviewStep from '@/components/setup-wizard/ReviewStep';

const STEPS = [
  { id: 'business_info', title: 'Business Info', description: 'Basic shop information' },
  { id: 'service_config', title: 'Services', description: 'Service types and templates' },
  { id: 'inventory', title: 'Inventory', description: 'Asset management setup' },
  { id: 'review', title: 'Review', description: 'Confirm and complete' }
];

export default function SetupWizard() {
  const [formData, setFormData] = useState<any>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { complete } = useOnboarding();

  // Load initial data once on mount
  useEffect(() => {
    async function init() {
      try {
        const res = await apiRequest('GET', '/api/v1/onboarding/status');
        const status = await res.json();
        
        // Determine which step to show - use onboarding_view_step if available, fallback to onboarding_step
        const viewStep = status.onboarding_view_step || status.onboarding_step;
        const stepIndex = viewStep 
          ? STEPS.findIndex(s => s.id === viewStep) 
          : 0;
        setCurrentStep(Math.max(0, stepIndex));
        
        // Load saved data or use defaults
        const onboardingData = status.onboarding_data || {};
        setFormData(onboardingData);
      } catch (err) {
        console.error('[SetupWizard] Failed to load onboarding status:', err);
      } finally {
        setIsLoading(false);
      }
    }
    init();
  }, []); // Only run once

  // Simple field update handler
  const updateField = (field: string, value: any) => {
    setFormData((prev: any) => {
      return { ...prev, [field]: value };
    });
  };

  // Validation functions
  // Validation state
  const [validationAttempted, setValidationAttempted] = useState<{[key: string]: boolean}>({});

  // Only validate when explicitly requested (not automatically on formData changes)
  // This prevents validation alerts on page load when form is empty
  
  const validateBusinessInfo = (data: any) => {
    const required = ['businessType', 'shopName', 'phone', 'email'];
    const missing = required.filter(field => !data[field] || data[field].trim() === '');
    
    const addressRequired = ['street', 'city', 'state', 'zipCode'];
    const addressMissing = addressRequired.filter(field => !data.address?.[field] || data.address[field].trim() === '');
    
    const numericRequired = [
      { key: 'defaultShopRate', valid: typeof data.defaultShopRate === 'number' ? data.defaultShopRate > 0 : Number(data.defaultShopRate) > 0 },
      { key: 'taxRate', valid: typeof data.taxRate === 'number' ? data.taxRate >= 0 : Number(data.taxRate) >= 0 },
    ];
    const numericMissing = numericRequired.filter(n => !n.valid).map(n => n.key);

    const allMissing = [...missing, ...addressMissing.map(field => `address.${field}`), ...numericMissing];
    
    if (allMissing.length > 0) {
      alert(`Please fill in the following required fields: ${allMissing.join(', ')}`);
      return false;
    }
    return true;
  };

  const validateServiceConfig = (data: any) => {
    if (data.serviceTypes && data.serviceTypes.length === 0) {
      alert('Please add at least one service type or select from the defaults.');
      return false;
    }
    
    if (data.hasInvalidTemplates) {
      alert('Please fix the template category issues before proceeding. Some selected templates have invalid categories.');
      return false;
    }
    
    return true;
  };

  const validateInventory = () => {
    return true;
  };

  const validateCurrentStep = () => {
    switch (STEPS[currentStep].id) {
      case 'business_info':
        return validateBusinessInfo(formData);
      case 'service_config':
        return validateServiceConfig(formData);
      case 'inventory':
        return validateInventory();
      case 'review':
        return true;
      default:
        return true;
    }
  };

  // Save and advance
  const handleNext = async () => {
    console.log('[SetupWizard] handleNext called with formData:', formData);
    
    // Validate current step before proceeding
    const currentStepId = STEPS[currentStep].id;
    if (currentStepId === 'business_info') {
      const isValid = validateBusinessInfo(formData);
      if (!isValid) {
        // Trigger validation attempt to highlight all required fields
        setValidationAttempted(prev => ({ ...prev, business_info: true }));
        alert('Please fix the validation errors before proceeding.');
        return;
      }
    }
    
    if (!validateCurrentStep()) return;
    
    console.log('[SetupWizard] Saving data to server...');
    setIsSaving(true);
    try {
      const endpoint = `/api/v1/onboarding/${STEPS[currentStep].id}`;
      console.log('[SetupWizard] POST to endpoint:', endpoint);
      console.log('[SetupWizard] POST data:', formData);
      await apiRequest('POST', endpoint, formData);
      console.log('[SetupWizard] Save successful, advancing to next step');
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      
      // Update the view step on the server
      try {
        await apiRequest('POST', '/api/v1/onboarding/view-step', { 
          step: STEPS[newStep].id 
        });
      } catch (err) {
        console.error('Failed to update view step:', err);
      }
    } catch (err: any) {
      console.error('[SetupWizard] Failed to save:', err);
      try {
        const raw = typeof err?.message === 'string' ? err.message : '';
        let parsed: any = null;
        try { parsed = raw ? JSON.parse(raw) : null; } catch {}
        if (parsed && (parsed.error === 'ValidationError' || parsed.missing || parsed.missingSettings)) {
          const missing = Array.isArray(parsed.missing) ? parsed.missing : [];
          const missingSettings = Array.isArray(parsed.missingSettings) ? parsed.missingSettings : [];
          const details = [...missing, ...missingSettings.map((k: string) => `shopSettings.${k}`)];
          alert(`Please complete required fields before continuing: ${details.join(', ')}`);
        } else if (raw) {
          alert(raw);
        } else {
          alert('Unable to save. Please check required fields and try again.');
        }
      } catch {
        alert('Unable to save. Please check required fields and try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Go back without saving (data is already saved on Next)
  const handlePrevious = async () => {
    const newStep = currentStep - 1;
    setCurrentStep(newStep);
    
    // Update the view step on the server
    try {
      await apiRequest('POST', '/api/v1/onboarding/view-step', { 
        step: STEPS[newStep].id 
      });
    } catch (err) {
      console.error('Failed to update view step:', err);
    }
  };


  // Complete setup
  const handleComplete = async () => {
    // Validate all steps before completing
    const businessInfoValid = validateBusinessInfo(formData);
    
    if (!businessInfoValid) {
      console.log('[SetupWizard] Business info validation failed');
      alert('Please fix the validation errors before completing setup.');
      return;
    }
    
    setIsSaving(true);
    try {
      await complete(formData);
      // Redirect will be handled by the OnboardingContext and App component
    } catch (err) {
      console.error('Failed to complete onboarding:', err);
      alert('Failed to complete setup. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const renderStep = () => {
    
    switch (STEPS[currentStep].id) {
      case 'business_info':
        const businessProps = {
          businessType: formData.businessType || '',
          shopName: formData.shopName || '',
          phone: formData.phone || '',
          email: formData.email || '',
          website: formData.website || '',
          address: formData.address || { street: '', city: '', state: '', zipCode: '' },
          shopHours: formData.shopHours || '',
          defaultShopRate: formData.defaultShopRate || 100,
          taxRate: formData.taxRate || 8.5,
          currency: formData.currency || 'USD'
        };
        return (
          <BusinessInfoStep 
            {...businessProps}
            onBusinessTypeChange={(val) => updateField('businessType', val)}
            onShopNameChange={(val) => updateField('shopName', val)}
            onPhoneChange={(val) => updateField('phone', val)}
            onEmailChange={(val) => updateField('email', val)}
            onWebsiteChange={(val) => updateField('website', val)}
            onAddressChange={(val) => updateField('address', val)}
            onShopHoursChange={(val) => updateField('shopHours', val)}
            onDefaultShopRateChange={(val) => updateField('defaultShopRate', val)}
            onTaxRateChange={(val) => updateField('taxRate', val)}
            onCurrencyChange={(val) => updateField('currency', val)}
            validationAttempted={validationAttempted.business_info}
          />
        );
      case 'service_config':
        return (
          <ServiceConfigStep 
            serviceTypes={formData.serviceTypes || []}
            serviceCategories={formData.serviceCategories || []}
            serviceRequestStatuses={formData.serviceRequestStatuses || []}
            selectedTemplates={formData.selectedTemplates || []}
            templateOverrides={formData.templateOverrides || {}}
            businessType={formData.businessType || ''}
            onServiceTypesChange={(val) => updateField('serviceTypes', val)}
            onServiceCategoriesChange={(val) => updateField('serviceCategories', val)}
            onServiceRequestStatusesChange={(val) => updateField('serviceRequestStatuses', val)}
            onSelectedTemplatesChange={(val) => updateField('selectedTemplates', val)}
            onTemplateOverridesChange={(val) => updateField('templateOverrides', val)}
            onInvalidTemplatesChange={(val) => updateField('hasInvalidTemplates', val)}
          />
        );
      case 'inventory':
        return (
          <InventorySetupStep 
            categories={formData.categories || []}
            assetTypes={formData.assetTypes || []}
            skipInventory={formData.skipInventory || false}
            onCategoriesChange={(val) => updateField('categories', val)}
            onAssetTypesChange={(val) => updateField('assetTypes', val)}
            onSkipInventoryChange={(val) => updateField('skipInventory', val)}
          />
        );
      case 'review':
        return (
          <ReviewStep 
            data={formData} 
            onEditStep={(stepIndex) => setCurrentStep(stepIndex)} 
          />
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  if (isLoading) return <div className="p-8">Loading setup...</div>;

  const currentStepData = STEPS[currentStep] || STEPS[0];
  const progress = ((currentStep + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-background">
      {/* Setup Navbar */}
      <SetupNavbar />
      
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Welcome to Motiv</h1>
              <p className="text-muted-foreground">Let&apos;s set up your shop in just a few steps</p>
            </div>
            <div className="text-sm text-muted-foreground">
              Step {currentStep + 1} of {STEPS.length}
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4">
            <Progress value={progress} className="h-2" />
            <div className="flex justify-between mt-2 text-sm text-muted-foreground">
              {STEPS.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                    index <= currentStep 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted'
                  }`}>
                    {index < currentStep ? <Check className="w-3 h-3" /> : index + 1}
                  </div>
                  <span className="ml-2 hidden sm:inline">{step.title}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-6 py-8">
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{currentStepData.title}</CardTitle>
                <p className="text-muted-foreground">{currentStepData.description}</p>
              </div>
              {STEPS[currentStep].id === 'service_config' && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    // This will be handled by the ServiceConfigStep component
                    const event = new CustomEvent('resetDefaults');
                    window.dispatchEvent(event);
                  }}
                >
                  Reset to Defaults
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {renderStep()}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6 max-w-4xl mx-auto">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          <div className="flex gap-2">
            {currentStep === STEPS.length - 1 ? (
              <Button
                onClick={handleComplete}
                disabled={isSaving}
                className="bg-green-600 hover:bg-green-700"
              >
                {isSaving ? 'Completing...' : 'Complete Setup'}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={isSaving || (currentStepData.id === 'service_config' && formData.hasInvalidTemplates)}
                title={currentStepData.id === 'service_config' && formData.hasInvalidTemplates ? 'Please fix template category issues before proceeding' : ''}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}